import { useCallback, useEffect, useRef, useState } from "react";
import { loadGoogleMaps } from "../lib/maps";

/**
 * Address field backed by the current Places API (`AutocompleteSuggestion`), which replaces the
 * retired `google.maps.places.Autocomplete` widget. We keep our own input and dropdown so the field
 * stays on-brand and works right-to-left; Google only supplies the suggestions and the place details.
 *
 * Suggestions are restricted to Morocco and biased to the recipient's city. If the key, the network
 * or the library fails, the field degrades to a plain text input and the buyer can still type.
 */
export const AddressAutocomplete = ({
  apiKey,
  value,
  onChange,
  onSelect,      // called with { line1, city, postal, lat, lng, formatted }
  city,          // { lat, lng } — used to bias the suggestions
  placeholder,
  testid,
  autoComplete = "address-line1",
}) => {
  const libRef = useRef(null);
  const inputRef = useRef(null);
  const tokenRef = useRef(null);
  const timerRef = useRef(null);
  const boxRef = useRef(null);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!apiKey) return undefined;
    loadGoogleMaps(apiKey)
      .then(async (google) => {
        const places = await google.maps.importLibrary("places");
        if (!cancelled) libRef.current = places;
      })
      .catch(() => { /* plain input fallback */ });
    return () => { cancelled = true; };
  }, [apiKey]);

  useEffect(() => {
    const away = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", away);
    return () => document.removeEventListener("mousedown", away);
  }, []);

  const fetchSuggestions = useCallback(async (input) => {
    const places = libRef.current;
    if (!places || input.trim().length < 3) { setItems([]); return; }
    if (!tokenRef.current) tokenRef.current = new places.AutocompleteSessionToken();
    const request = {
      input,
      sessionToken: tokenRef.current,
      includedRegionCodes: ["ma"],
      language: document.documentElement.lang || "en",
    };
    if (city?.lat && city?.lng) {
      request.locationBias = { center: { lat: city.lat, lng: city.lng }, radius: 20000 };
    }
    try {
      const { suggestions } = await places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
      setItems((suggestions || []).slice(0, 5).map((s) => s.placePrediction).filter(Boolean));
      setOpen(true);
    } catch (_) {
      // Suggestions unavailable (e.g. "Places API (New)" not enabled on the key yet) — the buyer
      // can still type the address by hand; see Admin → Settings → Maps for the one-time setup.
      setItems([]);
      setOpen(false);
    }
  }, [city?.lat, city?.lng]);

  const type = (text) => {
    onChange(text);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => fetchSuggestions(text), 250);
  };

  const pick = async (prediction) => {
    setOpen(false);
    setItems([]);
    tokenRef.current = null;   // a selection closes the billing session
    const label = prediction.text?.text || prediction.mainText?.text || "";
    onChange(label);
    try {
      const place = prediction.toPlace();
      await place.fetchFields({ fields: ["addressComponents", "formattedAddress", "location", "displayName"] });
      const comps = place.addressComponents || [];
      const findComp = (type_) => (comps.find((c) => (c.types || []).includes(type_)) || {}).longText || "";
      const line1 = [findComp("street_number"), findComp("route")].filter(Boolean).join(" ").trim()
        || place.displayName || place.formattedAddress || label;
      onChange(line1);
      onSelect?.({
        line1,
        city: findComp("locality") || findComp("administrative_area_level_2"),
        postal: findComp("postal_code"),
        lat: place.location?.lat() ?? null,
        lng: place.location?.lng() ?? null,
        formatted: place.formattedAddress || line1,
      });
    } catch (_) {
      onSelect?.({ line1: label, city: "", postal: "", lat: null, lng: null, formatted: label });
    }
  };

  return (
    <div className="relative" ref={boxRef}>
      <input
        ref={inputRef}
        data-testid={testid}
        className="field"
        autoComplete={autoComplete}
        placeholder={placeholder}
        value={value}
        onChange={(e) => type(e.target.value)}
        onFocus={() => items.length && setOpen(true)}
      />
      {open && items.length ? (
        <ul className="absolute z-30 inset-x-0 top-full mt-1 bg-plaster border border-brass-light shadow-lg max-h-64 overflow-y-auto"
            data-testid={`${testid}-suggestions`}>
          {items.map((prediction, i) => (
            <li key={`${prediction.placeId || i}`}>
              <button type="button" data-testid={`${testid}-suggestion-${i}`}
                      className="w-full text-start px-3 py-2 text-sm text-ink hover:bg-petal/30 transition-colors duration-150"
                      onClick={() => pick(prediction)}>
                <span className="block">{prediction.mainText?.text || prediction.text?.text}</span>
                {prediction.secondaryText?.text ? (
                  <span className="block text-xs text-muted">{prediction.secondaryText.text}</span>
                ) : null}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
};

export default AddressAutocomplete;
